import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/auth/AuthProvider";

interface UseDocumentGenerationProps {
  stepTitle: string;
  caseId: string;
  onSuccess: () => void;
  onError: () => void;
}

export function useDocumentGeneration({
  stepTitle,
  caseId,
  onSuccess,
  onError
}: UseDocumentGenerationProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const { user } = useAuth();

  const generateDocument = useCallback(async () => {
    if (!user?.id) {
      throw new Error("Du må være logget inn for å generere dokumenter.");
    }

    setIsGenerating(true);

    try {
      console.log("Generating document with RAG...");
      const { data: generatedData, error: generationError } = await supabase.functions
        .invoke('generate-document-rag', {
          body: {
            title: stepTitle,
            content: "",
            documentType: 'workflow_generated',
            caseId: caseId
          },
        });

      if (generationError) {
        console.error('Error generating document:', generationError);
        throw generationError;
      }

      if (!generatedData?.content) {
        throw new Error("No content was generated");
      }

      console.log('Saving generated document');
      const { error: saveError } = await supabase
        .from('documents')
        .insert({
          title: stepTitle,
          content: generatedData.content,
          document_type: 'workflow_generated',
          case_id: caseId,
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (saveError) {
        console.error('Error saving document:', saveError);
        throw saveError;
      }

      console.log("Document saved successfully");
      onSuccess();
    } catch (error) {
      console.error('Error in document generation:', error);
      onError();
    } finally {
      setIsGenerating(false);
    }
  }, [stepTitle, caseId, user?.id, onSuccess, onError]);

  return {
    generateDocument,
    isGenerating
  };
}